/**
 * Live password strength meter for the signup form.
 *
 * Mirrors Symfony's PasswordStrengthValidator::estimateStrength(), which is what
 * SilverStripe's EntropyPasswordValidator uses server side. Keeping the same
 * algorithm means the meter never says "OK" for a password the server rejects.
 *
 * See vendor/symfony/validator/Constraints/PasswordStrengthValidator.php
 */

const SCORE_LABELS = ['Very weak', 'Weak', 'Medium', 'Strong', 'Very strong']

/**
 * @param {string} password
 * @returns {number} 0-4, matching PasswordStrength::STRENGTH_*
 */
export function estimateStrength(password) {
  // Symfony works on bytes, not code points, so match that for non-ASCII input
  const bytes = new TextEncoder().encode(password)
  const length = bytes.length

  if (!length) {
    return 0
  }

  const distinct = new Set(bytes)
  let control = 0
  let digit = 0
  let upper = 0
  let lower = 0
  let symbol = 0
  let other = 0

  distinct.forEach((chr) => {
    if (chr < 32 || chr === 127) {
      control = 33
    } else if (chr >= 48 && chr <= 57) {
      digit = 10
    } else if (chr >= 65 && chr <= 90) {
      upper = 26
    } else if (chr >= 97 && chr <= 122) {
      lower = 26
    } else if (chr >= 128) {
      other = 128
    } else {
      symbol = 33
    }
  })

  const pool = lower + upper + digit + symbol + control + other
  const chars = distinct.size
  const entropy = chars * Math.log2(pool) + (length - chars) * Math.log2(chars)

  if (entropy >= 120) return 4
  if (entropy >= 100) return 3
  if (entropy >= 80) return 2
  if (entropy >= 60) return 1
  return 0
}

function initMeter(meter) {
  const field = document.getElementById(meter.dataset.passwordStrengthFor)

  if (!field) {
    return
  }

  const minScore = parseInt(meter.dataset.passwordStrength, 10) || 2
  const bar = meter.querySelector('.password-strength__bar-fill')
  const text = meter.querySelector('.password-strength__text')
  const icon = meter.querySelector('.password-strength__icon')

  let announced = null

  const update = () => {
    const empty = field.value.length === 0
    const score = estimateStrength(field.value)
    const met = !empty && score >= minScore

    meter.dataset.score = String(score)
    meter.classList.toggle('password-strength--met', met)

    if (bar) {
      bar.style.width = empty ? '0' : `${(score / 4) * 100}%`
    }

    if (icon) {
      // toggleAttribute, not .hidden — SVGElement does not implement the
      // hidden property, so assigning to it silently does nothing
      icon.toggleAttribute('hidden', !met)
    }

    // Only rewrite the live region when the message actually changes, so typing
    // doesn't queue an announcement per keystroke
    const message = empty
      ? ''
      : `${SCORE_LABELS[score]}${met ? ' — strong enough' : ' — not strong enough yet'}`

    if (text && message !== announced) {
      text.textContent = message
      announced = message
    }
  }

  field.addEventListener('input', update)
  update()
}

function init() {
  document.querySelectorAll('[data-password-strength]').forEach(initMeter)
}

// Guarded so the module can be imported outside a browser (the parity test runs
// it under node to check it still agrees with the PHP validator)
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
}